import {
    BarChart2,
    BookOpen,
    Calculator,
    LayoutDashboard,
    PieChart,
    Settings,
    TrendingUp,
    Zap,
    X
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { user } = useAuth();
  
  const navItems = [
    { path: "/", label: "Dashboard", icon: LayoutDashboard },
    { path: "/trades", label: "Trades", icon: TrendingUp },
    { path: "/positions", label: "Positions", icon: PieChart },
    { path: "/journal", label: "Journal", icon: BookOpen },
    { path: "/playbooks", label: "Playbook", icon: LayoutDashboard },
    { path: "/analytics", label: "Analytics", icon: BarChart2 },
    { path: "/option-lab", label: "Option Lab", icon: Calculator },
    { path: "/strategy", label: "Strategy", icon: Zap },
    { path: "/tools/position-sizer", label: "Position Sizer", icon: Calculator },
    { path: "/tools/import", label: "Import CSV", icon: Calculator },
    { path: "/brokers", label: "Brokers", icon: Settings },
  ];

  return (
    <aside 
      className={`
        fixed top-0 left-0 z-50 h-screen w-64 
        bg-slate-900 border-r border-slate-800
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'} 
        lg:translate-x-0
        shadow-2xl lg:shadow-none
      `}
    >
      <div className="flex flex-col h-full">
        {/* Logo Area */}
        <div className="h-16 flex items-center justify-between px-6 border-b border-slate-800 bg-slate-900/50 backdrop-blur-xl">
          <div className="flex items-center gap-2 font-bold text-xl text-emerald-400 bg-clip-text text-transparent bg-gradient-to-r from-emerald-400 to-cyan-400">
            <span className="text-2xl">📈</span>
            <span>TradeJournal</span>
          </div>
          <button 
            onClick={onClose}
            className="lg:hidden text-slate-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 py-6 px-4 space-y-1 overflow-y-auto custom-scrollbar">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => onClose()} 
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group
                  ${isActive 
                    ? 'bg-emerald-500/10 text-emerald-400 shadow-[0_0_20px_rgba(16,185,129,0.1)]' 
                    : 'text-slate-400 hover:text-slate-100 hover:bg-slate-800/50'
                  }
                `}
              >
                <Icon 
                  size={20} 
                  className={`
                    transition-transform duration-200 group-hover:scale-110
                    ${isActive ? 'text-emerald-400' : 'text-slate-500 group-hover:text-slate-300'}
                  `} 
                />
                <span className="font-medium">{item.label}</span>
                
                {isActive && (
                  <div className="ml-auto w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_10px_#34d399]" />
                )}
              </Link>
            );
          })}
        </nav>

        {/* User Profile / Footer */}
        <div className="p-4 border-t border-slate-800 bg-slate-900/50">
           <div className="flex items-center gap-3 px-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-emerald-500 to-cyan-500 flex items-center justify-center text-white font-bold text-sm">
                {user?.firstName?.charAt(0) || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-200 truncate">
                  {user?.firstName || 'User'}
                </p>
                <p className="text-[10px] text-slate-500">Free Plan</p>
              </div>
           </div>
           <div className="flex items-center justify-center gap-3 mt-3 pt-3 border-t border-slate-800/50">
             <Link to="/privacy" className="text-[10px] text-slate-600 hover:text-slate-400 transition-colors">Privacy</Link>
             <Link to="/terms" className="text-[10px] text-slate-600 hover:text-slate-400 transition-colors">Terms</Link>
             <Link to="/disclaimer" className="text-[10px] text-slate-600 hover:text-slate-400 transition-colors">Disclaimer</Link>
           </div>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
